import { useEffect, useState } from 'react';

import { getOrganizationById } from '@app/adapter/organization-service';
import { getUser } from '@app/adapter/user-service';
import { isError } from '@app/utils/error';

type Agreement = {
  agreedAt: string | undefined;
  email: string;
  organizationId: string;
};

/**
 * 常勤求人の成功報酬フィーについての利用規約の同意状況を取得する。
 * organizationSelectorのRecoil atomはログイン/リロード時に一覧取得APIから復元され、
 * customFieldsが最新でない場合があるため、getOrganizationByIdで組織の最新情報を取得し直す。
 * ProductManagement/OrderDetails等、FullTimeFeeNoticeの同意状況表示を行う複数画面で共通利用する。
 */
export function useFullTimeTermsAgreement(organizationId: string) {
  // 取得結果は取得元のorganizationIdと紐づけて保持し、現在の組織と一致する場合のみ公開する。
  // これにより組織切り替え直後や取得失敗時に、前の組織の値が表示されることを防ぐ。
  const [agreement, setAgreement] = useState<Agreement | undefined>(undefined);

  useEffect(() => {
    if (!organizationId) {
      return;
    }
    // cleanup時に立て、組織切り替えで不要になった古いリクエストの結果を破棄する
    let cancelled = false;
    void (async () => {
      try {
        const { data: latestOrganization } = await getOrganizationById(
          organizationId
        );
        const agreedAt = latestOrganization.customFields.fullTimeTermsAgreedAt;
        const agreedBy = latestOrganization.customFields.fullTimeTermsAgreedBy;
        const email = agreedBy ? (await getUser(agreedBy)).data.email : '';
        if (cancelled) {
          return;
        }
        // 同意日時とメールアドレスは揃ってから一度に反映する
        setAgreement({ agreedAt, email, organizationId });
      } catch (e) {
        if (isError(e)) {
          console.error(e.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const current =
    organizationId && agreement?.organizationId === organizationId
      ? agreement
      : undefined;

  return {
    agreedByEmail: current?.email ?? '',
    fullTimeTermsAgreedAt: current?.agreedAt,
  };
}
